/**
 * `AccessibilitySettings` — GVW-150 accessibility preferences.
 *
 * Per brief §2 / §8: P-3 council-room reduced-motion / contrast / density
 * controls. Reuses existing `useReducedMotion` hook (system pref display)
 * and persists user override via localStorage fallback under `gvw-set-a11y`.
 *
 * TODO(WP-E11): when `@geosoft/platform-sdk` exposes a real user-prefs API,
 * swap `localStorage` for `sdk.userPrefs.set('a11y', ...)`.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-150/brief.md
 */

import { useCallback, useEffect, useState } from 'react';
import { tokens } from '@/design';
import { useReducedMotion } from '@/design/hooks';
import { useLiveRegion } from '@/state/liveRegion';

interface A11yPrefs {
  density: 'medium' | 'compact';
  reducedMotion: 'system' | true | false;
  highContrast: 'system' | true | false;
}

const STORAGE_KEY = 'gvw-set-a11y';
const DEFAULTS: A11yPrefs = { density: 'medium', reducedMotion: 'system', highContrast: 'system' };

export function AccessibilitySettings() {
  const liveRegion = useLiveRegion();
  const systemReducedMotion = useReducedMotion();
  const [settings, setSettings] = useState<A11yPrefs>(DEFAULTS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) setSettings(JSON.parse(stored) as A11yPrefs);
    } catch {
      /* defaults */
    }
  }, []);

  useEffect(() => {
    document.title = 'Accessibility · GeoViewer';
    liveRegion.announce('status', 'Accessibility preferences loaded.');
  }, [liveRegion]);

  const handleSave = useCallback(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      setSaved(true);
      liveRegion.announce('status', `Density set to ${settings.density}. Settings saved.`);
    } catch {
      liveRegion.announce('alert', 'Could not save accessibility preferences.');
    }
  }, [settings, liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="gvw-150-heading"
      data-gvw-id="GVW-150"
      data-route="/workspace/settings/accessibility"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <h1
        id="gvw-150-heading"
        style={{
          margin: 0,
          marginBottom: tokens.space[4],
          fontSize: tokens.font.size['2xl'],
          fontWeight: tokens.font.weight.semibold,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
          accessibility_new
        </span>
        Accessibility preferences
      </h1>

      <p style={{ marginTop: 0, marginBottom: tokens.space[5], color: tokens.color.ink.secondary, fontSize: tokens.font.size.md }}>
        Respects your system preferences by default. Override below if needed.
      </p>

      {saved ? (
        <div
          role="status"
          style={{
            padding: tokens.space[3],
            marginBottom: tokens.space[4],
            background: tokens.color.surface[1],
            borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.success}`,
            borderRadius: tokens.radius.md,
          }}
        >
          Settings saved — applied immediately.
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[5], maxWidth: 560 }}
      >
        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, marginBottom: tokens.space[2] }}>
            Interface density
          </legend>
          <div role="radiogroup" aria-label="Density" style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[2] }}>
            <label style={{ display: 'flex', gap: tokens.space[2], alignItems: 'center' }}>
              <input
                type="radio"
                name="density"
                value="medium"
                checked={settings.density === 'medium'}
                onChange={() => setSettings((p) => ({ ...p, density: 'medium' }))}
              />
              <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, color: tokens.color.ink.secondary }}>
                density_medium
              </span>
              Medium (recommended)
            </label>
            <label style={{ display: 'flex', gap: tokens.space[2], alignItems: 'center' }}>
              <input
                type="radio"
                name="density"
                value="compact"
                checked={settings.density === 'compact'}
                onChange={() => setSettings((p) => ({ ...p, density: 'compact' }))}
              />
              <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, color: tokens.color.ink.secondary }}>
                density_small
              </span>
              Compact
            </label>
          </div>
        </fieldset>

        <div>
          <div style={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, marginBottom: tokens.space[2] }}>
            Reduce motion
          </div>
          <label style={{ display: 'flex', gap: tokens.space[2], alignItems: 'center' }}>
            <input
              type="checkbox"
              role="switch"
              aria-checked={settings.reducedMotion === 'system'}
              checked={settings.reducedMotion === 'system'}
              onChange={(e) => setSettings((p) => ({ ...p, reducedMotion: e.target.checked ? 'system' : false }))}
            />
            <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, color: tokens.color.ink.secondary }}>
              {systemReducedMotion ? 'motion_photos_off' : 'motion_photos_on'}
            </span>
            Match system preference (currently {systemReducedMotion ? 'reduced' : 'allowed'})
          </label>
        </div>

        <div>
          <div style={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, marginBottom: tokens.space[2] }}>
            High contrast
          </div>
          <label style={{ display: 'flex', gap: tokens.space[2], alignItems: 'center' }}>
            <input
              type="checkbox"
              role="switch"
              aria-checked={settings.highContrast === 'system'}
              checked={settings.highContrast === 'system'}
              onChange={(e) => setSettings((p) => ({ ...p, highContrast: e.target.checked ? 'system' : false }))}
            />
            <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, color: tokens.color.ink.secondary }}>
              contrast
            </span>
            Match system contrast preference (Phase 3 token plumbing)
          </label>
        </div>

        <div>
          <button
            type="submit"
            style={{
              padding: `${tokens.space[3]}px ${tokens.space[5]}px`,
              background: tokens.color.accent.primary,
              color: tokens.color.accent.on,
              border: 'none',
              borderRadius: tokens.radius.md,
              fontSize: tokens.font.size.md,
              fontWeight: tokens.font.weight.medium,
              cursor: 'pointer',
            }}
          >
            Save preferences
          </button>
        </div>
      </form>
    </main>
  );
}
